import axios from 'axios';

import { useAppStore } from '@/store/app.store';
import type { UserDto } from '@/types/api';

import { api } from './api';

// Sin fallback hardcodeado a propósito — un dominio adivinado puede quedar
// mal (pasó: se asumió app.ecocont.pe cuando el real es gateway.ecocont.pe).
const ECOCONT_GATEWAY_URL = import.meta.env.VITE_ECOCONT_GATEWAY_URL as string | undefined;

/**
 * SSO desde ecocont_ui: al redirigir a esta app, ecocont_ui adjunta su propio
 * JWT en el fragment de la URL (`#eco_sso_token=...`, nunca en query — no
 * viaja al servidor). Lo canjeamos contra el gateway (que ya valida ese JWT y
 * hace el exchange vía `/api/v1/auth/exchange`) en vez de mostrar el login
 * nativo. Si algo falla (token vencido, gateway caído), seguimos con el flujo
 * normal de hidratación del store persistido / login.
 */
const SSO_TOKEN_PATTERN = /eco_sso_token=([^&]+)/;

export async function tryEcocontSso(): Promise<boolean> {
  const match = SSO_TOKEN_PATTERN.exec(window.location.hash);
  const rawToken = match?.[1];
  if (!rawToken) return false;

  // Limpiar el fragment de inmediato — no debe quedar en el historial del navegador,
  // incluso si no podemos completar el exchange abajo (ver guard de ECOCONT_GATEWAY_URL).
  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  if (!ECOCONT_GATEWAY_URL) return false;

  try {
    const ecoToken = decodeURIComponent(rawToken);
    const { data } = await axios.get<{ accessToken: string; refreshToken: string; tenantId: string }>(
      `${ECOCONT_GATEWAY_URL}/tributario/_sso-token`,
      { headers: { Authorization: `Bearer ${ecoToken}` } },
    );

    const { data: user } = await api.get<UserDto>('/auth/me', {
      headers: { Authorization: `Bearer ${data.accessToken}` },
    });

    const { startNewSession, setRefreshToken, setActiveTenant, setSsoOrigin } = useAppStore.getState();
    startNewSession(data.accessToken, user);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    setSsoOrigin(true);
    if (data.tenantId) setActiveTenant(data.tenantId);
    return true;
  } catch {
    return false;
  }
}
